import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { CALCULATOR_LIST } from "../components/calculators/calculators";

const CATEGORIES = ["All", "Investments", "Loans", "Savings", "Planning", "Tax", "Basics"];

export default function CalculatorsPage() {
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("All");
    const list = useMemo(() => {
        const s = q.trim().toLowerCase();
        return CALCULATOR_LIST.filter((c) => {
            const matchCat = cat === "All" || c.category === cat;
            const matchQ = !s || c.name.toLowerCase().includes(s) || c.tagline.toLowerCase().includes(s);
            return matchCat && matchQ;
        });
    }, [q, cat]);

    return (
        <div>
            <Navbar />
            <section className="mesh-bg">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
                    <div className="max-w-2xl">
                        <div className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-3">Calculator Hub</div>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-slate-900 dark:text-white">
                            25+ calculators. <span className="gradient-text">Zero fluff.</span>
                        </h1>
                        <p className="mt-4 text-slate-600 dark:text-slate-300 text-base sm:text-lg">
                            Plan SIPs, EMIs, retirement, debt-payoff, FIRE, taxes and more — with real-time charts and shareable links.
                        </p>
                    </div>

                    <div className="mt-10 flex flex-col md:flex-row gap-3 items-stretch">
                        <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <input
                                data-testid="calc-search"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search calculators…"
                                className="input-soft !pl-11"
                            />
                        </div>
                        <div className="flex gap-2 overflow-x-auto scroll-fade pb-1">
                            {CATEGORIES.map((c) => (
                                <button
                                    key={c}
                                    data-testid={`cat-${c.toLowerCase()}`}
                                    onClick={() => setCat(c)}
                                    className={`px-4 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all border ${
                                        cat === c
                                            ? "bg-emerald-600 text-white border-emerald-600"
                                            : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-emerald-500"
                                    }`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {list.map((c) => (
                        <Link
                            to={`/calculators/${c.slug}`}
                            key={c.slug}
                            data-testid={`calc-tile-${c.slug}`}
                            className="card-soft p-6 group"
                        >
                            <div className="flex items-start justify-between">
                                <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white font-display font-bold text-sm">
                                    {c.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                                </div>
                                <span className="text-[10px] uppercase tracking-widest bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-full px-2 py-0.5 font-semibold">
                                    {c.category}
                                </span>
                            </div>
                            <h3 className="mt-5 font-display text-lg font-bold text-slate-900 dark:text-white">{c.name}</h3>
                            <p className="mt-1 text-sm text-slate-500 line-clamp-2">{c.tagline}</p>
                            <div className="mt-5 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 group-hover:gap-2 transition-all">
                                Open calculator <ArrowRight className="h-3 w-3" />
                            </div>
                        </Link>
                    ))}
                </div>
                {list.length === 0 && (
                    <div className="text-center text-slate-500 py-20">No calculators match your search.</div>
                )}
            </section>
            <Footer />
        </div>
    );
}
